import React from 'react';
import './About.css';
import Section from './Section.js';
import { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import {defaultConfig} from '../Data/defaultConfig';

    function About({config = defaultConfig }) {
      const [ref, inView] = useInView();
      const baseSize = config.font_size;

      const themes = [
        { icon: '🌿', title: 'Ecological Memory', desc: 'How landscapes hold the imprint of centuries' },
        { icon: '🏠', title: 'Displacement & Return', desc: 'The ache of leaving and the courage of coming home' },
        { icon: '📜', title: 'Oral Tradition', desc: 'Stories passed through generations, bending but unbroken' },
        { icon: '🌾', title: 'Land & Identity', desc: 'How place shapes who we become' }
      ];

      return (
        <Section id="about" className="pattern-dots py-20 md:py-28" bg="">
          <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`text-center mb-16 ${inView ? 'animate-fade-up' : 'opacity-start'}`}>
              <span className="inline-block font-body text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: config.primary_action_color }}>
                About the Book
              </span>
              <h2 className="font-display font-bold mb-4" style={{ fontFamily: `${config.font_family}, 'Playfair Display', Georgia, serif`, fontSize: `${baseSize * 2.25}px`, color: config.text_color }}>
                A Journey Unpacked
              </h2>
              <div className="w-16 h-1 mx-auto rounded-full" style={{ background: `linear-gradient(90deg, ${config.primary_action_color}, #F4A261)` }}/>
            </div>

            <div className="grid md:grid-cols-2 gap-12 lg:gap-16 mb-16">
              <div className={`space-y-5 ${inView ? 'animate-slide-left delay-200' : 'opacity-start'}`}>
                <p className="font-body leading-relaxed" style={{ fontSize: `${baseSize * 1.05}px`, color: config.text_color + 'dd' }}>
                  My birth-given middle name, Markeba, has always been a bit of an enigma in my life. It was given to me by my mom, inspired by a neighbor/friend who was reading a book. My understanding is that my first and middle names are from this book. Having unique names like Sontae and Markeba, which are often reimagined and mispronounced, can sometimes make them difficult to share. People often struggled with my first name, so I rarely mentioned my middle name, keeping it mostly to myself as something personal and almost burdensome.
                </p>
                <p className="font-body leading-relaxed" style={{ fontSize: `${baseSize * 1.05}px`, color: config.text_color + 'dd' }}>
                  As I got older, especially after my divorce, I legally dropped my middle name when I switched back to my birth last name. At the time, I didn’t understand the name or its significance to me, and it seemed easier not to use it. But like roots that run deep and quietly hold things together, Markeba remained a part of me, even when I tried to let it go.
                </p>
                <p className="font-body leading-relaxed" style={{ fontSize: `${baseSize * 1.05}px`, color: config.text_color + 'dd' }}>
                  Later on, as I worked through personal reflections and wrote the passages you'll soon read, I found myself curious enough to search for the meanings behind my middle name. To my surprise, I discovered there were many ways to spell and interpret it. Some definitions are connected to ideas, like light, spirit, and body, especially in reference to a word called Merkaba. The meanings I found reflected my own journey, different parts coming together to create something strong, unique, and full of purpose.
                </p>
              </div>
              <div className={`${inView ? 'animate-slide-right delay-300' : 'opacity-start'}`}>
                <div className="rounded-2xl p-8" style={{ background: config.surface_color, border: `1px solid ${config.primary_action_color}15`, boxShadow: `0 8px 30px ${config.text_color}08` }}>
                  <h3 className="font-display font-semibold mb-6" style={{ fontFamily: `${config.font_family}, 'Playfair Display', Georgia, serif`, fontSize: `${baseSize * 1.25}px`, color: config.text_color }}>
                    Key Themes
                  </h3>
                  <div className="space-y-4">
                    {themes.map((t, i) => (
                      <div key={i} className="flex items-start gap-4 p-3 rounded-xl transition-colors" style={{ background: `${config.primary_action_color}06` }}
                        onMouseEnter={e => e.currentTarget.style.background = `${config.primary_action_color}12`}
                        onMouseLeave={e => e.currentTarget.style.background = `${config.primary_action_color}06`}
                      >
                        <span className="text-2xl mt-0.5 w-fit">{t.icon}</span>
                        <div>
                          <span className="font-body font-semibold block" style={{ fontSize: `${baseSize * 0.95}px`, color: config.text_color }}>{t.title}</span>
                          <span className="font-body" style={{ fontSize: `${baseSize * 0.85}px`, color: config.text_color + '99' }}>{t.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={`rounded-2xl p-8 md:p-10 ${inView ? 'animate-fade-up delay-400' : 'opacity-start'}`} style={{ background: config.surface_color, border: `1px solid ${config.primary_action_color}15` }}>
              <h3 className="font-display font-semibold mb-6" style={{ fontFamily: `${config.font_family}, 'Playfair Display', Georgia, serif`, fontSize: `${baseSize * 1.2}px`, color: config.text_color }}>
                Publication Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: 'Release Date', value: 'March 30, 2025' },
                  { label: 'Publisher', value: 'Barnes & Noble' },
                  { label: 'Pages', value: '76' },
                  { label: 'ISBN', value: '9 798279684618' }
                ].map((d, i) => (
                  <div key={i}>
                    <span className="font-body text-xs font-semibold tracking-widest uppercase block mb-1" style={{ color: config.primary_action_color }}>{d.label}</span>
                    <span className="font-body font-medium" style={{ fontSize: `${baseSize * 0.95}px`, color: config.text_color }}>{d.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${config.primary_action_color}10` }}>
                <span className="w-fit inline-block px-4 py-1.5 rounded-full text-xs font-semibold font-body" style={{ background: '#9CAF8820', color: '#6B8F5B' }}>
                  Autobiography · Emotional Healing · Personal Growth
                </span>
              </div>
            </div>
          </div>
        </Section>
  );
}

export default About;